// B3-#9 MUGdo 充值回调页
//
// 用户从支付提供商页面跳回后落到 /#/recharge/callback?order_id=<uuid>;
// 本页轮询 GET /gapi/user/recharges/{order_id}(2s 一次,最多 60s),展示
// 充值结果(成功 / 待支付 / 失败 / 超时)。
//
// 配套 F40SLz 浮窗版本(参见 components/recharge-status-overlay.tsx)用于
// 用户提交订单后不离开当前页等回执的场景。

import { useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { apiClient, ApiClientError, cn } from '@dogdisk/shared'
import { AppLayout } from '../components/app-layout'
import { MobileScreen } from '../components/mobile-screen'

type RechargeStatus = {
  order_id: string
  status: 'pending' | 'paid' | 'cancelled' | 'failed' | 'expired'
  amount_cny_cents: number | null
  amount_coin: number | null
  paid_at: string | null
}

const POLL_INTERVAL_MS = 2000
const POLL_MAX_ATTEMPTS = 30 // 60s 上限

export function RechargeCallbackPage() {
  const [sp] = useSearchParams()
  const orderId = sp.get('order_id') ?? ''
  const navigate = useNavigate()
  // location.key 用作 effect 依赖:Playwright `page.goto` 同 URL 也会 push 新 history
  // entry → location.key 变 → 重启轮询(否则同 hash 路由不重挂载,UI 卡上次终态)。
  const location = useLocation()

  const [data, setData] = useState<RechargeStatus | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [attempts, setAttempts] = useState(0)
  const [done, setDone] = useState(false)
  // 用 ref 让 setInterval 闭包能读到当前 done,避免捕获初始值后无限 tick。
  const doneRef = useRef(false)

  useEffect(() => {
    // 每次 orderId / location 重置:状态、ref 全部回零(确保切 mock status 时
    // UI 不卡上次 paid/failed 终态)。
    setData(null)
    setError(null)
    setAttempts(0)
    setDone(false)
    doneRef.current = false

    if (!orderId) {
      setError('缺少 order_id 参数')
      setDone(true)
      doneRef.current = true
      return
    }
    let cancelled = false

    const tick = async () => {
      try {
        const res = await apiClient.get<RechargeStatus>(`/gapi/user/recharges/${orderId}`)
        if (cancelled) return
        setData(res)
        if (res.status !== 'pending') {
          setDone(true)
          doneRef.current = true
          return
        }
      } catch (e) {
        if (cancelled) return
        if (e instanceof ApiClientError) setError(e.message || '查询订单失败')
        else setError(e instanceof Error ? e.message : '查询订单失败')
        setDone(true)
        doneRef.current = true
        return
      }
      setAttempts((n) => n + 1)
    }

    void tick()

    const id = window.setInterval(() => {
      if (cancelled || doneRef.current) return
      setAttempts((n) => {
        if (n + 1 > POLL_MAX_ATTEMPTS) {
          setDone(true)
          doneRef.current = true
          return n
        }
        return n
      })
      if (!cancelled && !doneRef.current) void tick()
    }, POLL_INTERVAL_MS)

    return () => {
      cancelled = true
      window.clearInterval(id)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId, location.key])

  const isPending = !done && (data?.status === 'pending' || data == null)
  const isTimeout = done && attempts > POLL_MAX_ATTEMPTS && data?.status === 'pending'

  const view = useMemo(() => {
    if (error) return { kind: 'error' as const, title: '查询失败', desc: error }
    if (isTimeout)
      return {
        kind: 'pending' as const,
        title: '处理中…',
        desc: '订单仍在确认,稍后可在「订单记录」中查看结果',
      }
    if (isPending) return { kind: 'pending' as const, title: '等待支付确认…', desc: `已查询 ${attempts} 次` }
    switch (data?.status) {
      case 'paid':
        return {
          kind: 'success' as const,
          title: '充值成功',
          desc: data.amount_coin
            ? `到账 ${data.amount_coin.toLocaleString()} 金币`
            : data.amount_cny_cents
              ? `已支付 ¥${(data.amount_cny_cents / 100).toFixed(2)}`
              : '到账成功',
        }
      case 'cancelled':
        return { kind: 'error' as const, title: '订单已取消', desc: '本次充值未完成' }
      case 'failed':
        return { kind: 'error' as const, title: '支付失败', desc: '请重新发起充值或联系客服' }
      case 'expired':
        return { kind: 'error' as const, title: '订单已过期', desc: '请重新创建充值订单' }
      default:
        return { kind: 'pending' as const, title: '查询中', desc: '' }
    }
  }, [error, isTimeout, isPending, data, attempts])

  // 2 frame 共用本 file:MUGdo=成功视图 / F40SLz=失败/pending/超时视图(按 ?status= 分支)
  const frameTestId = view.kind === 'success' ? 'MUGdo' : 'F40SLz'

  return (
    <AppLayout>
      <MobileScreen>
        <main data-frame-id={frameTestId} data-testid={frameTestId} className="flex flex-1 flex-col items-center justify-center px-6 pb-20">
          <span
            className={cn(
              'flex h-20 w-20 items-center justify-center rounded-full',
              view.kind === 'success' && 'bg-[#ebfaf6] text-[#19caad]',
              view.kind === 'pending' && 'bg-[#FFF7E6] text-[#F59E0B]',
              view.kind === 'error' && 'bg-[#FEE2E2] text-[#FF6B6B]',
            )}
          >
            {view.kind === 'success' ? (
              <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4"><path d="M5 12l5 5 9-12" /></svg>
            ) : view.kind === 'pending' ? (
              <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" className="animate-spin">
                <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
              </svg>
            ) : (
              <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4"><path d="M6 6l12 12M18 6l-12 12" /></svg>
            )}
          </span>
          <h1 className="mt-6 text-[20px] font-semibold text-[#1A1A1A]">{view.title}</h1>
          {view.desc && <p className="mt-2 text-center text-[13px] text-[#9CA3AF]">{view.desc}</p>}
          {orderId && (
            <p className="mt-4 font-mono text-[11px] text-[#9CA3AF]">订单 # {orderId.slice(0, 8)}</p>
          )}

          <div className="mt-8 flex w-full max-w-[300px] flex-col gap-2">
            <button
              type="button"
              onClick={() => navigate('/orders')}
              className="h-12 w-full rounded-lg bg-[#19caad] text-[14px] font-semibold text-white active:bg-[#14a18b]"
            >
              查看订单记录
            </button>
            <button
              type="button"
              onClick={() => navigate('/wallet')}
              className="h-12 w-full rounded-lg bg-[#F6F7F8] text-[14px] font-semibold text-[#1A1A1A]"
            >
              回到钱包
            </button>
          </div>
        </main>
      </MobileScreen>
    </AppLayout>
  )
}
